<template>
    <div class="login-page" :class="{'login-page--embedded': embedded}">
        <section class="login-brand">
            <div class="login-brand__inner">
                <img src="../assets/logo_argus_full.png" alt="Argus" class="login-brand__logo">
                <h1 class="login-brand__title">ИТ - экосистема, созданная строителями</h1>
                <div class="login-brand__note">
                    <i class="fas fa-shield-alt"></i>
                    <span>Страница входа для сотрудников компаний</span>
                </div>
            </div>
        </section>

        <section class="login-panel">
            <div class="login-card">
                <div v-if="!challenge" class="login-card__header">
                    <h2>{{ isCompanyStep ? 'Выберите компанию' : 'Вход' }}</h2>
                    <p v-if="!isCompanyStep">Используйте логин и пароль или вход через Telegram.</p>
                    <p v-else>Для этой учетной записи доступно несколько компаний.</p>
                </div>

                <b-alert v-if="displayedErrorMessage" show variant="danger" class="login-alert">
                    {{ displayedErrorMessage }}
                </b-alert>

                <TelegramChallenge v-if="challenge" :initial="challenge" @authenticated="afterSuccess" @cancel="challenge = null"/>
                <b-form v-else-if="!isCompanyStep" @submit.prevent="onSubmit">
                    <b-form-group label="Логин" label-for="loginInput">
                        <b-input-group>
                            <b-input-group-prepend is-text>
                                <i class="fas fa-user"></i>
                            </b-input-group-prepend>
                            <b-form-input
                                id="loginInput"
                                v-model.trim="form.login"
                                type="text"
                                required
                                placeholder="Введите логин"
                                autocomplete="username"
                                :disabled="isLoading"
                            ></b-form-input>
                        </b-input-group>
                    </b-form-group>

                    <b-form-group label="Пароль" label-for="passwordInput">
                        <b-input-group>
                            <b-input-group-prepend is-text>
                                <i class="fas fa-lock"></i>
                            </b-input-group-prepend>
                            <b-form-input
                                id="passwordInput"
                                v-model="form.password"
                                :type="passwordInputType"
                                required
                                placeholder="Введите пароль"
                                autocomplete="current-password"
                                :disabled="isLoading"
                            ></b-form-input>
                            <b-input-group-append>
                                <b-button
                                    variant="outline-secondary"
                                    type="button"
                                    title="Показать пароль"
                                    :disabled="isLoading"
                                    @click="togglePasswordVisibility"
                                >
                                    <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>

                    <ForeignDeviceOption v-model="foreignDevice"/>
                    <b-button
                        type="submit"
                        variant="success"
                        block
                        class="login-submit"
                        :disabled="loginButtonDisabled"
                    >
                        <b-spinner v-if="isLoading && loginMode === 'password'" small class="mr-2"></b-spinner>
                        {{ passwordCooldownActive ? `Повторить через ${passwordCooldownText}` : 'Войти' }}
                    </b-button>

                    <div class="login-divider">
                        <span>или</span>
                    </div>

                    <div class="login-telegram" :class="{ 'login-telegram--disabled': isLoading }">
                        <div class="login-telegram__button-mask">
                            <vue-telegram-login
                                mode="callback"
                                :telegram-login="telegramLoginBot"
                                radius="6"
                                @callback="onTelegramCallback"
                            />
                        </div>
                    </div>
                </b-form>

                <b-form v-else @submit.prevent="continueWithCompany">
                    <b-form-group label="Компания" label-for="companyInput">
                        <b-form-select
                            id="companyInput"
                            v-model="form.company"
                            :options="companyOptions"
                            :disabled="isLoading"
                        ></b-form-select>
                    </b-form-group>
                    <div class="login-company-actions">
                        <b-button
                            type="submit"
                            variant="success"
                            :disabled="!form.company || isLoading || passwordCooldownActive"
                        >
                            <b-spinner v-if="isLoading" small class="mr-2"></b-spinner>
                            {{ passwordCooldownActive ? `Повторить через ${passwordCooldownText}` : 'Продолжить' }}
                        </b-button>
                        <b-button
                            type="button"
                            variant="outline-secondary"
                            :disabled="isLoading"
                            @click="backToCredentials"
                        >
                            Назад
                        </b-button>
                    </div>
                </b-form>
            </div>
        </section>
    </div>
</template>

<script>
import {vueTelegramLogin} from 'vue-telegram-login';
import TelegramChallenge from '@/components/Security/TelegramChallenge.vue';
import {acceptSession, securityState} from '@/utils/sessionSecurity';
import ForeignDeviceOption from '@/components/Security/ForeignDeviceOption.vue';
import {
    cooldownDeadlineFromError,
    cooldownSecondsRemaining,
    formatCooldown
} from '@/utils/loginCooldown';

const TELEGRAM_LOGIN_BOT = process.env.VUE_APP_TELEGRAM_LOGIN_BOT || 'ArgusVision_Bot';

export default {
    name: "Login",
    props: {embedded: Boolean},
    components: {
        vueTelegramLogin, TelegramChallenge, ForeignDeviceOption
    },
    data() {
        return {
            challenge: null, foreignDevice: false,
            form: {
                login: '',
                password: '',
                company: null,
                tgUser: null
            },
            companyOptions: [],
            pendingPayload: null,
            isLoading: false,
            loginMode: null,
            showPassword: false,
            errorMessage: null,
            passwordCooldownIdentity: null,
            passwordCooldownUntil: 0,
            passwordCooldownSeconds: 0,
            passwordCooldownTimer: null
        };
    },
    computed: {
        isCompanyStep() {
            return this.companyOptions.length > 0;
        },
        loginButtonDisabled() {
            return this.isLoading || this.passwordCooldownActive || !this.form.login || !this.form.password;
        },
        passwordInputType() {
            return this.showPassword ? 'text' : 'password';
        },
        telegramLoginBot() {
            return TELEGRAM_LOGIN_BOT;
        },
        currentPasswordIdentity() {
            return `${String(this.form.login || '').trim().toLowerCase()}|${this.form.company || 'unspecified'}`;
        },
        passwordCooldownActive() {
            return this.passwordCooldownSeconds > 0 &&
                this.passwordCooldownIdentity === this.currentPasswordIdentity;
        },
        passwordCooldownText() {
            return formatCooldown(this.passwordCooldownSeconds);
        },
        displayedErrorMessage() {
            return this.passwordCooldownActive
                ? `Слишком много неудачных попыток. Повторите через ${this.passwordCooldownText}.`
                : this.errorMessage;
        }
    },
    beforeDestroy() {
        this.stopPasswordCooldownTimer();
    },
    mounted() {
        const pending = securityState.pendingLogin;
        if (!pending || this.embedded) return;
        securityState.pendingLogin = null;
        if (pending.result.challenge) this.challenge = pending.result;
        else if (pending.result.options) {
            this.companyOptions = pending.result.options.map(item => ({value: item.company, text: item.companyName}));
            this.pendingPayload = pending.payload; this.loginMode = 'telegram';
        }
    },
    methods: {
        onSubmit() {
            const payload = {
                login: this.form.login,
                password: this.form.password
            };
            this.login(payload, 'password');
        },
        onTelegramCallback(tgUser) {
            this.form.tgUser = tgUser;
            this.login({tgUser}, 'telegram');
        },
        continueWithCompany() {
            if (!this.pendingPayload || !this.form.company) {
                return;
            }
            this.login(this.pendingPayload, this.loginMode || 'company');
        },
        backToCredentials() {
            this.companyOptions = [];
            this.form.company = null;
            this.pendingPayload = null;
            this.errorMessage = null;
        },
        async login(payload, mode) {
            const {$store: {dispatch, commit}, form: {company}} = this;
            this.isLoading = true;
            this.loginMode = mode;
            this.errorMessage = null;
            this.pendingPayload = payload;

            try {
                const data = await dispatch('login', {payload: Object.assign({}, payload, {personal: !this.foreignDevice}), company});
                if (Array.isArray(data.options)) {
                    this.companyOptions = data.options.map(item => ({
                        value: item.company,
                        text: item.companyName
                    }));
                    this.form.company = null;
                    return;
                }
                this.afterSuccess(data);
            } catch (err) {
                commit('removeToken');
                commit('removeRefreshToken');
                if (err && err.response && err.response.status === 429) {
                    this.startPasswordCooldown(err);
                } else {
                    this.errorMessage = this.getErrorMessage(err);
                }
            } finally {
                this.isLoading = false;
            }
        },
        afterSuccess(result) {
            if (result.challenge) { this.challenge = result; return; }
            const {accessToken, refreshToken} = result;
            if (!accessToken || !refreshToken) {
                this.$store.commit('removeToken');
                this.$store.commit('removeRefreshToken');
                this.errorMessage = 'Не удалось выполнить вход.';
                return;
            }

            const {$store: {dispatch, commit}, $router, $route} = this;
            try { acceptSession('staff', result, true); } catch (error) { this.errorMessage = error.message; this.challenge = null; return; }
            commit('setToken', accessToken);
            commit('setRefreshToken', refreshToken);
            dispatch('setAuthorizationHeader', accessToken);
            dispatch('updateAbility', this);
            if (this.embedded) { this.$emit('authenticated'); return; }
            dispatch('abortWizard');

            const redirect = $route.query && $route.query.redirect && $route.query.redirect !== '/login'
                ? $route.query.redirect
                : '/';
            $router.replace(redirect);
        },
        togglePasswordVisibility() {
            this.showPassword = !this.showPassword;
        },
        startPasswordCooldown(error) {
            this.passwordCooldownIdentity = this.currentPasswordIdentity;
            this.passwordCooldownUntil = cooldownDeadlineFromError(error);
            this.errorMessage = null;
            this.refreshPasswordCooldown();
            this.stopPasswordCooldownTimer();
            this.passwordCooldownTimer = setInterval(this.refreshPasswordCooldown, 1000);
        },
        refreshPasswordCooldown() {
            this.passwordCooldownSeconds = cooldownSecondsRemaining(this.passwordCooldownUntil);
            if (!this.passwordCooldownSeconds) {
                this.stopPasswordCooldownTimer();
                this.passwordCooldownIdentity = null;
                this.passwordCooldownUntil = 0;
            }
        },
        stopPasswordCooldownTimer() {
            if (this.passwordCooldownTimer) clearInterval(this.passwordCooldownTimer);
            this.passwordCooldownTimer = null;
        },
        getErrorMessage(err) {
            const data = err && err.response ? err.response.data : null;
            if (data && data.msg) {
                return data.msg;
            }
            if (typeof data === 'string') {
                return data;
            }
            return 'Не удалось выполнить вход. Проверьте логин и пароль.';
        }
    }
};
</script>

<style scoped>
.login-page--embedded { display: block !important; min-height: 0 !important; }
.login-page--embedded .login-brand { display: none; }
.login-page--embedded .login-panel { padding: 0; }
.login-page {
    display: grid;
    grid-template-columns: minmax(280px, 0.72fr) minmax(400px, 1.28fr);
    min-height: 100vh;
    background: #f6f8fa;
    color: #212529;
}

.login-brand {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    background: #18232c;
    color: #f8fafc;
    box-shadow: inset -1px 0 0 rgba(255, 255, 255, .08);
}

.login-brand::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 5px;
    background: #48bb78;
    content: '';
}

.login-brand__inner {
    width: min(360px, 100%);
}

.login-brand__logo {
    display: block;
    width: 260px;
    max-width: 100%;
    height: auto;
    margin-bottom: 34px;
    filter: drop-shadow(0 14px 24px rgba(0, 0, 0, .26));
}

.login-brand__subtitle {
    margin: 0;
    color: #e2e8f0;
    font-size: 16px;
}

.login-brand__note {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-top: 46px;
    color: #c8d3de;
    font-size: 14px;
    line-height: 1.4;
}

.login-brand__note i {
    color: #68d391;
    font-size: 15px;
}

.login-panel {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
}

.login-card {
    width: min(430px, 100%);
    padding: 26px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 16px 38px rgba(31, 41, 51, .10);
}

.login-card__header {
    margin-bottom: 18px;
}

.login-card__header h2 {
    margin: 0 0 6px;
    font-size: 24px;
    font-weight: 700;
}

.login-card__header p {
    margin: 0;
    color: #6c757d;
}

.login-alert {
    margin-bottom: 14px;
}

.login-submit {
    margin-top: 8px;
}

.login-divider {
    position: relative;
    margin: 18px 0;
    text-align: center;
}

.login-divider::before {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: #dee2e6;
    content: '';
}

.login-divider span {
    position: relative;
    padding: 0 10px;
    background: #fff;
    color: #6c757d;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
}

.login-telegram {
    display: flex;
    justify-content: center;
    min-height: 42px;
}

.login-telegram__button-mask {
    display: inline-flex;
    overflow: hidden;
    border-radius: 6px;
    background: #54a9eb;
    line-height: 0;
}

.login-telegram__button-mask :deep(iframe) {
    display: block;
    border: 0;
    background: transparent;
}

.login-telegram--disabled {
    pointer-events: none;
    opacity: .58;
}

.login-company-actions {
    display: flex;
    gap: 8px;
}

.login-company-actions .btn {
    flex: 1 1 0;
}

@media (max-width: 860px) {
    .login-page {
        grid-template-columns: 1fr;
    }

    .login-brand {
        min-height: 210px;
        padding: 28px 22px;
        border-right: 0;
        border-bottom: 1px solid #121b22;
    }

    .login-brand__logo {
        width: 220px;
        margin-bottom: 18px;
    }

    .login-brand__note {
        margin-top: 20px;
    }

    .login-panel {
        align-items: flex-start;
        padding: 22px;
    }
}

@media (max-width: 480px) {
    .login-brand {
        justify-content: flex-start;
        min-height: 190px;
    }

    .login-card {
        padding: 18px;
    }

    .login-company-actions {
        flex-direction: column;
    }
}
</style>
